/**
 * DOCTOR-WORKSPACE-COMPLETION-V1 -- pure view helpers for the DOCTOR LENS
 * (the doctor-shaped Patient 360) and the doctor patient search.
 *
 * Pure by construction -- no React, no fetch, no Supabase, no Date.now (the
 * patient-360-view.ts doctrine) -- so every decision + label unit-tests
 * without jsdom. NOTHING here is an access decision (the backend doctor gate
 * + the /me signal are the boundary); these helpers are UX cleanliness only.
 *
 * REUSE LAW: the lens renders through the EXISTING Patient 360 render grammar
 * (patient-360-view.ts label maps, honest vocabulary, money formatter) and
 * the EXISTING consult door path -- never a second Patient 360 component
 * family, never a second money renderer, never a re-derivation.
 */

import {
  parseOriginToken,
  resolveOriginDoor,
  withOrigin,
  type OriginDoor,
} from '@/dashboard/return-to-origin';
import type { DashboardMe } from '@/dashboard/types';
import { CONTINUITY_INTAKE_ABSENT_COPY } from '@/dashboard/patients/patient-360-view';
import { consultPathForAppointment } from '@/dashboard/doctor/doctor-home-view';

// --- Nav-signal access decision (the #294 door pattern) --------------------

export type DoctorLensAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * The pure access decision for the doctor lens routes, keyed on the
 * SERVER-computed `me.doctorLensEnabled` signal (present ONLY for a scoped
 * doctor). While `/me` loads -> wait; on error / null / signal absent ->
 * redirect to the caller's own home (never a dead-end page, never a leak).
 * Mirrors decidePatientMasterAccess.
 */
export function decideDoctorLensAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): DoctorLensAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) return { kind: 'redirect', to: '/login' };
  if (state.me.doctorLensEnabled !== true) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// --- Route hrefs (doctor-scoped) -------------------------------------------

/** The doctor patient search route (the empty-day + nav door). */
export const DOCTOR_PATIENTS_HREF = '/dashboard/doctor/patients';

/** The doctor day home (the board the record is most often reached from). */
export const DOCTOR_DAY_HREF = '/dashboard/doctor';

// --- BUILD 3 (L-09): where the record was reached FROM -----------------------
// DOCTOR-CONSULT-CORRECTIVE-V1. The record's Back control said "Back to search"
// and led to the CONSTANT DOCTOR_PATIENTS_HREF, whatever door the doctor came
// through -- and three doors reach this record (the search page, the consult
// workspace's related rail, and the case-history rail). Label and behaviour
// disagreed for two of the three. The origin now travels in the URL through THE
// ONE return-to-origin primitive; no second constant is minted here.

/** The doors that reach the doctor patient record. Allowlist, never a URL. */
export const LENS_ORIGINS = ['search', 'consult', 'day'] as const;

export type LensOrigin = (typeof LENS_ORIGINS)[number];

/** Narrow a raw `?from=` to a known door (forged/absent -> null). */
export function parseLensOrigin(raw: string | null | undefined): LensOrigin | null {
  return parseOriginToken(LENS_ORIGINS, raw);
}

/** The doctor lens route for one patient (the search result / recent-patient
 *  door). Doctor-scoped path -- never the Patient Master record route.
 *  `origin` stamps the door travelled so the record's Back can return to it. */
export function doctorLensHref(patientId: string, origin?: LensOrigin): string {
  return withOrigin(
    `/dashboard/doctor/patient/${encodeURIComponent(patientId)}`,
    origin,
  );
}

/**
 * The record's Back door for the origin actually travelled.
 *
 * `consultAppointmentId` is the consult the doctor came from, when this render
 * can resolve one. A "consult" origin with no resolvable appointment degrades
 * to the search door AND relabels -- the label follows the behaviour, which is
 * exactly the defect (L-09) this closes.
 */
export function lensBackDoor(
  origin: LensOrigin | null,
  consultAppointmentId?: number | null,
): OriginDoor {
  const search: OriginDoor = {
    href: DOCTOR_PATIENTS_HREF,
    label: 'Back to search',
  };
  return resolveOriginDoor<LensOrigin>(
    {
      search: () => search,
      day: () => ({ href: DOCTOR_DAY_HREF, label: 'Back to my day' }),
      consult: () =>
        typeof consultAppointmentId === 'number'
          ? {
              href: `/dashboard/doctor/consult/${consultAppointmentId}`,
              label: 'Back to consult',
            }
          : null,
    },
    origin,
    search,
  );
}

// --- Continue-care door (reuses the consult path; check-in gate UNCHANGED) --

export interface ContinueCareView {
  /** consult / view -> a live door with a label + href; blocked -> an honest
   *  reason chip (no href); none -> nothing (continue care via the journey). */
  readonly kind: 'consult' | 'view' | 'blocked' | 'none';
  readonly label: string | null;
  /** The consult-workspace href when a lawful door exists; null otherwise. */
  readonly href: string | null;
  readonly reason: string | null;
}

/**
 * Map the server-computed continue-care fragment to a render view. The href
 * (when present) is the EXISTING consult workspace path -- the doctor lens
 * NEVER re-implements the consult route or the check-in gate; it only routes
 * to the lawful door the server already computed, or renders the honest
 * blocked reason.
 */
export function continueCareView(fragment: {
  readonly kind: 'consult' | 'view' | 'blocked' | 'none';
  readonly appointmentId: number | null;
  readonly label: string | null;
  readonly reason: string | null;
}): ContinueCareView {
  if (
    (fragment.kind === 'consult' || fragment.kind === 'view') &&
    fragment.appointmentId !== null
  ) {
    return {
      kind: fragment.kind,
      label: fragment.label,
      href: consultPathForAppointment(fragment.appointmentId),
      reason: null,
    };
  }
  if (fragment.kind === 'blocked') {
    return { kind: 'blocked', label: null, href: null, reason: fragment.reason };
  }
  return { kind: 'none', label: null, href: null, reason: null };
}

// --- Doctor search hint copy ------------------------------------------------

/** DOCTOR-HOME-STATE-MODEL-V1 (F5, founder order 2026-08-14): the front-desk
 *  posture -- Mobile / UMR / name. A mobile query is a fingerprint lookup;
 *  no contact value ever rides the result cards (structurally absent). The
 *  former phone-withheld hint is RETIRED with the refusal it explained. */
export const DOCTOR_SEARCH_HINT =
  'Search by mobile number, UMR (e.g. AC-000123), or patient name.';

/** SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T2): the doctor search's honest
 *  no-match copy -- ONE home, consumed by the shared search door (the
 *  doctor context of GlobalPatientSearchModal) and the legacy Patients-page
 *  tool alike. The doctor never registers: no register door renders in the
 *  doctor context (LES-016 -- a door only where its handler exists), and
 *  this copy names the real next actor instead. */
export const DOCTOR_SEARCH_NO_MATCH_TITLE = 'No patient found in your clinic.';
export const DOCTOR_SEARCH_NO_MATCH_BODY =
  'No registered patient in your clinic matches this mobile number, UMR, or name. If this is a new patient, the front desk registers them first - they appear here once registered.';

/** Contact stays masked on the doctor lens (founder ruling: masked contact;
 *  reveal is not a doctor action). The honest field copy. */
export const DOCTOR_CONTACT_MASKED_COPY =
  'Contact is masked on the doctor lens. Reveal is not a doctor action.';

// --- DOCTOR-WORKSPACE-EXPERIENCE-V2: the list + Recent Patients panels -------

/** Honest empty-state copy for the doctor Recent Patients panel (a genuine
 *  panel with a real read -- never a search-redirect door). */
export const DOCTOR_RECENT_PATIENTS_EMPTY_TITLE =
  'No recent patients at your clinic yet.';
export const DOCTOR_RECENT_PATIENTS_EMPTY_BODY =
  'Patients you have seen recently at this clinic appear here. Open a patient from Today or the patients list to review their care.';

/** Honest empty-state copy for the doctor clinic-patients LIST. */
export const DOCTOR_PATIENT_LIST_EMPTY_TITLE =
  'No patients registered at this clinic yet.';
export const DOCTOR_PATIENT_LIST_EMPTY_BODY =
  'Registered patients at your clinic appear here. The front desk registers new patients; they appear once registered.';

const LAST_SEEN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * A short, honest "last visit / last seen" label from an ISO timestamp: the
 * date literal only (e.g. "18 Jul 2026"), never invented; a missing / malformed
 * value renders the honest em-dash. Pure -- no timezone math (the date portion
 * is read as written; the exact clock time is not a doctor list fact).
 */
export function doctorLastSeenLabel(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.trim() === '') return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (match === null) return '-';
  const month = LAST_SEEN_MONTHS[Number(match[2]) - 1];
  if (month === undefined) return '-';
  return `${Number(match[3])} ${month} ${match[1]}`;
}

// A failed or withheld read must never claim that the patient supplied no intake.
export function historyIntakeAbsentCopy(
  availability: 'available' | 'unavailable' | 'withheld' | undefined,
): string {
  if (availability === 'available') return CONTINUITY_INTAKE_ABSENT_COPY;
  if (availability === 'withheld') return 'Previous intake withheld during identity review.';
  return 'Previous intake unavailable. Reload to try again.';
}
